import {
  HTTP_AUTH_HEADER_BOUNDARY_PATTERN,
  HTTP_AUTH_LEGACY_VALUE_WHITESPACE_PATTERN,
  HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN,
  HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN,
  HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN,
  HTTP_AUTH_SCHEME_PATTERN,
  HTTP_AUTH_SERIALIZED_QUOTE_PATTERN,
} from "../../packages/acp-core/src/structured-auth-redaction.js";
import type { RedactMatch, RedactPattern } from "./redact-pattern-runtime.js";
import { PEM_REDACT_PATTERN_SOURCE } from "./redact-pem.js";

export const PAYMENT_CREDENTIAL_ENV_KEYS = String.raw`CARD[_-]?NUMBER|CARD[_-]?CVC|CARD[_-]?CVV|CVC|CVV|SECURITY[_-]?CODE|PAYMENT[_-]?CREDENTIAL|SHARED[_-]?PAYMENT[_-]?TOKEN`;
export const PAYMENT_CREDENTIAL_QUERY_KEYS = String.raw`card[-_]?number|card[-_]?cvc|card[-_]?cvv|cvc|cvv|security[-_]?code|payment[-_]?credential|shared[-_]?payment[-_]?token`;
export const PAYMENT_CREDENTIAL_JSON_KEYS = String.raw`cardNumber|card_number|cardCvc|card_cvc|cardCvv|card_cvv|cvc|cvv|securityCode|security_code|paymentCredential|payment_credential|sharedPaymentToken|shared_payment_token`;
export const AWS_SECRET_ACCESS_KEY_FIELD_KEYS = String.raw`aws[-_]?secret[-_]?access[-_]?key|awsSecretAccessKey|SecretAccessKey`;
const AUTH_QUERY_KEYS = String.raw`access[-_]?token|auth[-_]?token|hook[-_]?token|refresh[-_]?token|id[-_]?token|api[-_]?key|apikey|client[-_]?secret|app[-_]?secret|private[-_]?key|${AWS_SECRET_ACCESS_KEY_FIELD_KEYS}|credential|authorization|token|key|secret|password|pass|passwd|auth|jwt|session|code|signature|x[-_]?amz[-_]?(?:signature|security[-_]?token)`;
export const FORM_BODY_FIRST_PAIR_KEYS = String.raw`${AUTH_QUERY_KEYS}|app[-_]?secret|credential|${PAYMENT_CREDENTIAL_QUERY_KEYS}`;
export const STANDALONE_ASSIGNMENT_SECRET_KEYS = String.raw`access_token|refresh_token|id_token|auth[-_]?token|hook[-_]?token|api[-_]?key|client[-_]?secret|app[-_]?secret|private[-_]?key|authorization|jwt|token|secret|password|pass|passwd|credential|${PAYMENT_CREDENTIAL_QUERY_KEYS}`;
export const CONFIG_ASSIGNMENT_SECRET_KEYS = String.raw`access[-_]?token|refresh[-_]?token|id[-_]?token|auth[-_]?token|hook[-_]?token|api[-_]?(?:key|secret)|client[-_]?secret|app[-_]?secret|private[-_]?key|secret[-_]?key|key[-_]?material|authorization|jwt|token|secret|password|passphrase|pass|passwd|credential|${PAYMENT_CREDENTIAL_QUERY_KEYS}`;
// Bare `pass` is ordinary prose far more often than a key ("tests now pass: older clients"), so the
// generic unquoted colon form skips it; BARE_PASS_ASSIGNMENT_MATCHER keeps config-shaped `pass:` masked, and
// `pass=`, `pass = `, namespaced, prefixed, and quoted forms are unchanged.
const CONFIG_COLON_ASSIGNMENT_SECRET_KEYS = String.raw`access[-_]?token|refresh[-_]?token|id[-_]?token|auth[-_]?token|hook[-_]?token|api[-_]?(?:key|secret)|client[-_]?secret|app[-_]?secret|private[-_]?key|secret[-_]?key|key[-_]?material|authorization|jwt|token|secret|password|passphrase|passwd|credential|${PAYMENT_CREDENTIAL_QUERY_KEYS}`;
const CONFIG_DIRECT_ASSIGNMENT_SECRET_KEYS = String.raw`access-token|refresh-token|id-token|auth-token|hook-token|api[-_]?(?:key|secret)|secret[-_]?key|key[-_]?material|passphrase`;
export const CONFIG_PREFIXED_PASSWORD_ASSIGNMENT_SECRET_KEYS = String.raw`password|passphrase|pass|passwd`;
const CLI_SECRET_FLAG_KEYS = String.raw`${AWS_SECRET_ACCESS_KEY_FIELD_KEYS}|api[-_]?key|hook[-_]?token|access[-_]?token|refresh[-_]?token|id[-_]?token|token|secret|password|passwd|credential|private[-_]?key|client[-_]?secret|${PAYMENT_CREDENTIAL_QUERY_KEYS}`;

export const FORM_BODY_KEY_INVISIBLE_CHARS = String.raw`\p{C}\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\u115F\u1160\u3164\uFFA0`;
const ENV_ASSIGNMENT_REDACT_PATTERN = String.raw`/\b[A-Z0-9_]*(?:KEY|TOKEN|SECRET|PASSWORD|PASSWD|${PAYMENT_CREDENTIAL_ENV_KEYS})\b\s*[=:]\s*(["']?)([^\s"'\\]+)\1/g`;
const ESCAPED_ENV_ASSIGNMENT_REDACT_PATTERN = String.raw`/\b[A-Z0-9_]*(?:KEY|TOKEN|SECRET|PASSWORD|PASSWD|${PAYMENT_CREDENTIAL_ENV_KEYS})\b\s*[=:]\s*\\+(["'])([^\s"'\\]+)\\+\1/g`;
// Quoted values may contain the other quote characters; only the matching closing quote ends
// the value. The unquoted variant accepts one leading quote so unterminated values still mask.
export const STANDALONE_ASSIGNMENT_QUOTED_REDACT_PATTERN = String.raw`(^|[\s,;({\["])(?:${STANDALONE_ASSIGNMENT_SECRET_KEYS})=(["'\x60])((?:(?!\2)[^\r\n])+)\2`;
const STANDALONE_ASSIGNMENT_REDACT_PATTERN = String.raw`(^|[\s,;({\["])(?:${STANDALONE_ASSIGNMENT_SECRET_KEYS})=(["'\x60]?[^\s&#"'\x60<>]+)`;
export const CONFIG_QUOTED_ASSIGNMENT_SECRET_KEYS = String.raw`access[-_]?token|refresh[-_]?token|id[-_]?token|auth[-_]?token|hook[-_]?token|api[-_]?(?:key|secret)|secret[-_]?key|key[-_]?material|authorization|jwt|token|secret|password|passphrase|pass|passwd|${PAYMENT_CREDENTIAL_QUERY_KEYS}`;
export const CONFIG_QUOTED_ASSIGNMENT_REDACT_PATTERN = String.raw`/(^|[\s,{])(?:(?:${CONFIG_QUOTED_ASSIGNMENT_SECRET_KEYS})(?:\s*:\s*|\s+=\s*|=\s*)|[a-z0-9][a-z0-9._-]{0,79}[-_](?:${CONFIG_PREFIXED_PASSWORD_ASSIGNMENT_SECRET_KEYS})\s*[:=]\s*|[a-z0-9_.-]{1,80}\.(?:${CONFIG_ASSIGNMENT_SECRET_KEYS})\s*[:=]\s*)(["'\x60])((?:(?!\2)[^\r\n])+)\2/g`;
const CONFIG_ASSIGNMENT_REDACT_PATTERN = String.raw`/(^|[\s,{])(?:(?:${CONFIG_COLON_ASSIGNMENT_SECRET_KEYS})\s*:\s*|(?:${CONFIG_ASSIGNMENT_SECRET_KEYS})(?:\s+=\s*|=\s+))([^\s#"'\x60<>]+)/g`;
const CONFIG_DIRECT_ASSIGNMENT_REDACT_PATTERN = String.raw`/(^|[\s,{])(?:${CONFIG_DIRECT_ASSIGNMENT_SECRET_KEYS})=([^\s#"'\x60<>]+)/g`;
const CONFIG_PREFIXED_PASSWORD_ASSIGNMENT_REDACT_PATTERN = String.raw`/(^|[\s,{])[a-z0-9][a-z0-9._-]{0,79}[-_](?:${CONFIG_PREFIXED_PASSWORD_ASSIGNMENT_SECRET_KEYS})\s*[:=]\s*([^\s#"'\x60<>]+)/g`;
const CONFIG_NAMESPACED_ASSIGNMENT_REDACT_PATTERN = String.raw`/(^|[\s,{])[a-z0-9_.-]{1,80}\.(?:${CONFIG_ASSIGNMENT_SECRET_KEYS})\s*[:=]\s*([^\s#"'\x60<>]+)/g`;
const STRUCTURED_JSON_SECRET_REDACT_PATTERN = String.raw`"(?:apiKey|api_key|apiToken|api_token|bearerToken|bearer_token|token|secret|password|passwd|${AWS_SECRET_ACCESS_KEY_FIELD_KEYS}|credential|authorization|proxy-authorization|cookie|set-cookie|x-api-key|x-auth-token|accessToken|access_token|refreshToken|refresh_token|idToken|id_token|authToken|auth_token|clientSecret|client_secret|privateKey|private_key|secret_value|raw_secret|secret_input|key_material)"\s*:\s*"([^"]+)"`;
const STRUCTURED_JSON_PAYMENT_REDACT_PATTERN = String.raw`"(?:${PAYMENT_CREDENTIAL_JSON_KEYS})"\s*:\s*"([^"]+)"`;
const AMBIGUOUS_QUOTED_SECRET_FIELD_REDACT_PATTERN = String.raw`(^|[\s,{])["']?(?:api[-_]key|access[-_]token|refresh[-_]token|id[-_]token|authToken|auth[-_]token|clientSecret|client[-_]secret|appSecret|app[-_]secret|private[-_]key|credential|authorization|secret[-_]value|raw[-_]secret|secret[-_]input|key[-_]material)["']?\s*[:=]\s*(["'])([^"'\r\n]+)\2`;
const AMBIGUOUS_QUOTED_AUTH_FIELD_REDACT_PATTERN = String.raw`(^|[\s,{])["']?(?:authorization|proxy-authorization|cookie|set-cookie|x-api-key|x-auth-token)["']?\s*[:=]\s*(["'])([^"'\r\n]+)\2`;
// Pure-base64 prefixes require a non-alphanumeric boundary and skip explicit data-URL payloads.
// Match the token first: the lookbehind rescans the whole base64 run, quadratic per `+`/`/`/`=`.
const BASE64_SAFE_TOKEN_BOUNDARY = String.raw`(^|[^A-Za-z0-9])`;
export const base64SafeToken = (token: string) =>
  String.raw`${BASE64_SAFE_TOKEN_BOUNDARY}(?=${token})(?<!;base64,[A-Za-z0-9+/=]*)(${token})`;
export const IDENTIFIER_SAFE_TOKEN_BOUNDARY = String.raw`(^|[^A-Za-z0-9_])`;

function isAwsValueCharacter(char: string): boolean {
  const code = char.charCodeAt(0);
  return (
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    (code >= 48 && code <= 57) ||
    char === "/" ||
    char === "+" ||
    char === "="
  );
}

const AWS_SECRET_ACCESS_KEY_VALUE_RE =
  /(?=[A-Za-z0-9/+=]{0,39}[A-Z])(?=[A-Za-z0-9/+=]{0,39}[a-z])(?=[A-Za-z0-9/+=]{0,39}[0-9/+=])(?=[A-Za-z0-9/+=]{0,39}[G-Zg-z/+=])[A-Za-z0-9/+=]{40}/u;
function couldMatchAwsSecretAccessKey(text: string): boolean {
  // A delimiter invalidates every 40-character window containing it. Search backwards
  // within each candidate window, then skip directly past that delimiter.
  for (let end = 39; end < text.length;) {
    let cursor = end;
    while (cursor > end - 40 && isAwsValueCharacter(text[cursor]!)) {
      cursor--;
    }
    if (cursor === end - 40) {
      return AWS_SECRET_ACCESS_KEY_VALUE_RE.test(text);
    }
    end = cursor + 40;
  }
  return false;
}

const AWS_VALUE_WHITESPACE_RE = /\s/;
const AWS_URL_SCHEME_CHARACTER_RE = /[A-Za-z0-9+.-]/;
const AWS_URL_SCHEME_START_RE = /[A-Za-z]/;

function* matchAwsSecretAccessKeys(text: string): Iterable<RedactMatch> {
  if (!couldMatchAwsSecretAccessKey(text)) {
    return;
  }
  type UrlContext = {
    start: number;
    end: number;
    authorityEnd: number;
    portStart: number;
    portValid: boolean;
    hasAt: boolean;
    queryOrFragment: boolean;
    closing: string | undefined;
    closingEscapeDepth: number;
  };
  const closingDelimiters: Readonly<Record<string, string>> = {
    '"': '"',
    "'": "'",
    "`": "`",
    "<": ">",
    "(": ")",
    "[": "]",
    "{": "}",
  };
  let runStart = -1;
  let schemeStart = -1;
  let url: UrlContext | undefined;
  let backslashes = 0;
  const runs: Array<{ start: number; end: number; origin: number; url: UrlContext | undefined }> =
    [];
  for (let index = 0; index <= text.length; index++) {
    const char = text[index] ?? "";
    const whitespace = index === text.length || AWS_VALUE_WHITESPACE_RE.test(char);
    const escapeDepth = backslashes;
    backslashes = char === "\\" ? backslashes + 1 : 0;
    if (char && isAwsValueCharacter(char)) {
      if (runStart === -1) {
        runStart = index;
      }
    } else if (runStart !== -1) {
      if (index - runStart === 40) {
        runs.push({ start: runStart, end: index, origin: runStart, url });
      } else if (index - runStart > 40 && char === "@" && text[index - 41] === "/") {
        runs.push({ start: index - 40, end: index, origin: runStart, url });
      }
      runStart = -1;
    }
    if (whitespace) {
      if (url) {
        url.end = Math.min(url.end, index);
      }
      for (const run of runs) {
        const context = run.url;
        const publicUrl =
          context &&
          !context.hasAt &&
          context.portValid &&
          (context.portStart === -1 ||
            (context.authorityEnd === -1 ? context.end : context.authorityEnd) >
              context.portStart + 1);
        const before = text[run.start - 1] ?? "";
        const after = text[run.end] ?? "";
        const slashCredential = run.start !== run.origin;
        // Preserve the existing malformed credential spanning a numeric port and path.
        // Its terminal @ qualifies that whole run, not userinfo in the URL's path.
        const portCredential =
          context &&
          context.portStart !== -1 &&
          run.origin === context.portStart + 1 &&
          context.authorityEnd > run.origin &&
          run.end > context.authorityEnd &&
          after === "@";
        if (
          (!slashCredential && (before === "_" || isAwsValueCharacter(before))) ||
          after === "_" ||
          text.slice(run.origin - 8, run.origin) === ";base64," ||
          (publicUrl && !portCredential && run.start >= context.start && run.end <= context.end)
        ) {
          continue;
        }
        const match = text.slice(run.start, run.end);
        if (AWS_SECRET_ACCESS_KEY_VALUE_RE.test(match)) {
          yield { match, groups: [match], input: text, offset: run.start };
        }
      }
      runs.length = 0;
      schemeStart = -1;
      url = undefined;
      continue;
    }
    if (url && index >= url.start) {
      // A quoted/marked value ends independently of punctuation inside raw userinfo.
      if (char === url.closing && escapeDepth === url.closingEscapeDepth) {
        url.end = Math.min(url.end, index);
        url = undefined;
      } else {
        if ("?#\"'<>`|()[]{}".includes(char)) {
          url.end = Math.min(url.end, index);
        }
        url.queryOrFragment ||= char === "?" || char === "#";
        if (url.authorityEnd === -1) {
          url.hasAt ||= char === "@";
          if ("/?#".includes(char)) {
            url.authorityEnd = index;
          } else if (index < url.end) {
            if (char === ":") {
              url.portValid &&= url.portStart === -1;
              url.portStart = index;
            } else if (url.portStart !== -1 && (char < "0" || char > "9")) {
              url.portValid = false;
            }
          }
        }
      }
    }
    if (AWS_URL_SCHEME_CHARACTER_RE.test(char)) {
      if (schemeStart === -1) {
        schemeStart = index;
      }
    } else {
      if (
        (!url || (index >= url.end && !url.queryOrFragment)) &&
        char === ":" &&
        text.startsWith("//", index + 1) &&
        schemeStart !== -1 &&
        AWS_URL_SCHEME_START_RE.test(text[schemeStart]!)
      ) {
        const opening = text[schemeStart - 1];
        let closingEscapeDepth = 0;
        for (let offset = schemeStart - 2; offset >= 0 && text[offset] === "\\"; offset--) {
          closingEscapeDepth++;
        }
        url = {
          start: index + 3,
          end: text.length,
          authorityEnd: -1,
          portStart: -1,
          portValid: true,
          hasAt: false,
          queryOrFragment: false,
          closing: opening ? closingDelimiters[opening] : undefined,
          closingEscapeDepth,
        };
      }
      schemeStart = -1;
    }
  }
}

export const AWS_SECRET_ACCESS_KEY_MATCHER = Object.freeze({
  source: "aws-secret-access-key",
  exec: matchAwsSecretAccessKeys,
  couldMatch: couldMatchAwsSecretAccessKey,
});

// Bare `pass:` reads as prose only when a plain word or number and one space precede it on a line that
// carries no assignment before it ("The tests now pass: older clients", "12 pass: 0 fail"). A spaced
// `key: value` phrase earlier on the line is how prose introduces lists ("Release notes: all suites
// pass: nothing else changed"), so it is not an assignment; an inline `user: bot pass: x` record is the
// accepted cost, while `user=bot pass: x` and `user:bot pass: x` stay records. Every other
// position is a record start and masks exactly like the generic assignment rule did: line or text start,
// YAML `- `, punctuation, tabs, or an inline `key=value` / `key:value` field of any spacing or length.
// Namespaced (`smtp.pass:`) and prefixed (`db-pass:`, `db_pass:`) keys belong to their own patterns but
// still make their line a record, as does any other credential key with a colon in any letter case
// (`password: x pass: y`, `Authorization: Bearer x pass: y`);
// `pass` inside another word (`bypass:`) is not a key. Whitespace around
// the colon may include line breaks, as in the generic rule, so YAML explicit keys and indented
// continuations stay covered. One forward pass classifies every key, so the cost is linear in the text
// regardless of line length or key count.
const BARE_PASS_KEY_RE = new RegExp(
  String.raw`(?<![A-Za-z0-9])(pass|${CONFIG_COLON_ASSIGNMENT_SECRET_KEYS})\s*:\s*`,
  "gi",
);
const BARE_PASS_VALUE_RE = /[^\s#"'\x60<>]+/y;
const ASCII_WORD_CHAR_RE = /[A-Za-z0-9]/;
const INLINE_WHITESPACE_RE = /[ \t\r\n]/;

function* matchBarePassAssignments(text: string): Iterable<RedactMatch> {
  if (!/pass\s*:/.test(text)) {
    return;
  }
  const keys = [...text.matchAll(BARE_PASS_KEY_RE)];
  if (keys.length === 0) {
    return;
  }
  let next = 0;
  let assignmentSeen = false;
  for (let index = 0; index < text.length; index++) {
    const key = keys[next];
    if (!key) {
      return;
    }
    if (index === key.index) {
      next++;
      const before = text[index - 1] ?? "";
      const owned = key[1] !== "pass" || before === "." || before === "-" || before === "_";
      const prose =
        !owned &&
        !assignmentSeen &&
        before === " " &&
        ASCII_WORD_CHAR_RE.test(text[index - 2] ?? "");
      if (!prose) {
        // A key, this rule's or a namespaced one's, makes the rest of its value's line a record, so a
        // second `pass:` masks too. Skip the key so a line break after its colon does not reset that.
        assignmentSeen = true;
        let end = index + key[0].length;
        if (!owned) {
          BARE_PASS_VALUE_RE.lastIndex = end;
          const value = BARE_PASS_VALUE_RE.exec(text)?.[0];
          if (value) {
            end += value.length;
            yield { match: text.slice(index, end), groups: [value], input: text, offset: index };
          }
        }
        // Keys inside the skipped span were consumed as value text.
        let following = keys[next];
        while (following && following.index < end) {
          next++;
          following = keys[next];
        }
        index = end - 1;
        continue;
      }
    }
    const char = text[index];
    if (char === "\n" || char === "\r") {
      assignmentSeen = false;
    } else if (char === "=") {
      assignmentSeen = true;
    } else if (char === ":" && !INLINE_WHITESPACE_RE.test(text[index + 1] ?? " ")) {
      assignmentSeen = true;
    }
  }
}

const BARE_PASS_ASSIGNMENT_MATCHER = Object.freeze({
  source: "bare-pass-assignment",
  exec: matchBarePassAssignments,
});
const TELEGRAM_BOT_TOKEN_REDACT_PATTERN = String.raw`\bbot(\d{6,}:[A-Za-z0-9_-]{20,})\b`;
const TELEGRAM_TOKEN_REDACT_PATTERN = String.raw`\b(\d{6,}:[A-Za-z0-9_-]{20,})\b`;
const CREDENTIAL_STYLE_HEADER_KEYS = "x-goog-api-key|api-key|apikey|x-api-token|x-access-token";
const GATEWAY_SECURITY_HEADER_KEYS =
  "X-OpenClaw-Token|x-pomerium-jwt-assertion|X-Api-Key|X-Auth-Token";
// Colons identify HTTP headers. Equals assignments may be form bodies, so stop only before an
// actual following `&key=` pair; otherwise opaque credential punctuation stays fully masked.
const LOG_HEADER_BOUNDARY_PATTERN = String.raw`(^|[^A-Za-z0-9_?&-]|\\{1,64}[rn])`;
const CREDENTIAL_STYLE_COLON_HEADER_REDACT_PATTERN = String.raw`${LOG_HEADER_BOUNDARY_PATTERN}(?:${CREDENTIAL_STYLE_HEADER_KEYS})${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*:${HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}([^\s\\"',;]+)`;
const CREDENTIAL_STYLE_EQUALS_ASSIGNMENT_REDACT_PATTERN = String.raw`${LOG_HEADER_BOUNDARY_PATTERN}(?:${CREDENTIAL_STYLE_HEADER_KEYS})${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*=${HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}([^\s\\"',;]+)`;
const GATEWAY_SECURITY_COLON_HEADER_REDACT_PATTERN = String.raw`${LOG_HEADER_BOUNDARY_PATTERN}(?:${GATEWAY_SECURITY_HEADER_KEYS})\s*:\s*([^\s"',;]+)`;
const GATEWAY_SECURITY_EQUALS_ASSIGNMENT_REDACT_PATTERN = String.raw`${LOG_HEADER_BOUNDARY_PATTERN}(?:${GATEWAY_SECURITY_HEADER_KEYS})\s*=\s*([^\s"',;]+)`;
const HTTP_AUTH_HEADER_REDACT_PATTERNS = [
  String.raw`${HTTP_AUTH_HEADER_BOUNDARY_PATTERN}Proxy-Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}${HTTP_AUTH_SCHEME_PATTERN}${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})`,
  String.raw`${HTTP_AUTH_HEADER_BOUNDARY_PATTERN}Proxy-Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})[ \t]*(?=${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}(?:$|[,;)}\]]|\r?\n(?![ \t])))`,
  String.raw`${HTTP_AUTH_HEADER_BOUNDARY_PATTERN}Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}(?!(?:Bearer|Basic|Bot)(?=${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}))${HTTP_AUTH_SCHEME_PATTERN}${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})`,
  String.raw`${HTTP_AUTH_HEADER_BOUNDARY_PATTERN}Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_OPTIONAL_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}(?!(?:Bearer|Basic|Bot)(?=${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}))(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})[ \t]*(?=${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}(?:$|[,;)}\]]|\r?\n(?![ \t])))`,
  CREDENTIAL_STYLE_COLON_HEADER_REDACT_PATTERN,
  CREDENTIAL_STYLE_EQUALS_ASSIGNMENT_REDACT_PATTERN,
] as const;
const AUTHORIZATION_BEARER_REDACT_PATTERN = String.raw`Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_LEGACY_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}Bearer${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})`;
const AUTHORIZATION_BASIC_REDACT_PATTERN = String.raw`Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_LEGACY_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}Basic${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})`;
const AUTHORIZATION_BOT_REDACT_PATTERN = String.raw`Authorization${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}[ \t]*[:=]${HTTP_AUTH_LEGACY_VALUE_WHITESPACE_PATTERN}${HTTP_AUTH_SERIALIZED_QUOTE_PATTERN}Bot${HTTP_AUTH_REQUIRED_VALUE_WHITESPACE_PATTERN}(${HTTP_AUTH_OPAQUE_CREDENTIAL_PATTERN})`;
const STANDALONE_BEARER_REDACT_PATTERN = String.raw`\bBearer\s+([-A-Za-z0-9._~+/=]{18,})(?![-A-Za-z0-9._~+/=])`;
const DEFAULT_REDACT_FIELD_PATTERNS: readonly RedactPattern[] = [
  ENV_ASSIGNMENT_REDACT_PATTERN,
  ESCAPED_ENV_ASSIGNMENT_REDACT_PATTERN,
  STRUCTURED_JSON_SECRET_REDACT_PATTERN,
  STRUCTURED_JSON_PAYMENT_REDACT_PATTERN,
  AMBIGUOUS_QUOTED_SECRET_FIELD_REDACT_PATTERN,
  AMBIGUOUS_QUOTED_AUTH_FIELD_REDACT_PATTERN,
  String.raw`--(?:${CLI_SECRET_FLAG_KEYS})=([^\s"']+)`,
  String.raw`--(?:${CLI_SECRET_FLAG_KEYS})\s+(?!(?:or|and)\b(?=\s+--))(["']?)([^\s"']+)\1`,
  AUTHORIZATION_BEARER_REDACT_PATTERN,
  AUTHORIZATION_BASIC_REDACT_PATTERN,
  AUTHORIZATION_BOT_REDACT_PATTERN,
  ...HTTP_AUTH_HEADER_REDACT_PATTERNS,
  GATEWAY_SECURITY_COLON_HEADER_REDACT_PATTERN,
  GATEWAY_SECURITY_EQUALS_ASSIGNMENT_REDACT_PATTERN,
  STANDALONE_BEARER_REDACT_PATTERN,
  String.raw`\b(?:https?|wss?|ftp):\/\/[^\/\s:@]*:([^\/\s@]+)@`,
  String.raw`\b(?:postgres(?:ql)?|mysql|mongodb(?:\+srv)?|rediss?|amqps?):\/\/[^:\s/@]*:([^@\s]+)@`,
  String.raw`(^|[\s,;])(?:${FORM_BODY_FIRST_PAIR_KEYS})=([^&\s]+)(?=&[A-Za-z_][A-Za-z0-9_.-]*=)`,
  STANDALONE_ASSIGNMENT_QUOTED_REDACT_PATTERN,
  STANDALONE_ASSIGNMENT_REDACT_PATTERN,
  CONFIG_QUOTED_ASSIGNMENT_REDACT_PATTERN,
  CONFIG_ASSIGNMENT_REDACT_PATTERN,
  // Runs where the generic rule handled bare `pass:`, before the namespaced and prefixed rules.
  BARE_PASS_ASSIGNMENT_MATCHER,
  CONFIG_DIRECT_ASSIGNMENT_REDACT_PATTERN,
  CONFIG_PREFIXED_PASSWORD_ASSIGNMENT_REDACT_PATTERN,
  CONFIG_NAMESPACED_ASSIGNMENT_REDACT_PATTERN,
  PEM_REDACT_PATTERN_SOURCE,
  String.raw`(^|[\s,{])["']?(?:${AWS_SECRET_ACCESS_KEY_FIELD_KEYS})["']?\s*[:=]\s*(["']?)([A-Za-z0-9/+=]{40})(?![A-Za-z0-9/+=])\2`,
];

export const VENDOR_TOKEN_REDACT_PATTERNS: readonly string[] = [
  String.raw`\b(sk-[A-Za-z0-9_-]{8,})\b`,
  String.raw`(ghp_[A-Za-z0-9]{10,})`,
  String.raw`(github_pat_[A-Za-z0-9_]{10,})`,
  String.raw`(gho_[A-Za-z0-9]{10,})`,
  String.raw`(ghu_[A-Za-z0-9]{10,})`,
  String.raw`(ghs_[A-Za-z0-9]{10,})`,
  String.raw`(ghr_[A-Za-z0-9]{10,})`,
  String.raw`(glpat-[A-Za-z0-9._=\-]{20,})`,
  String.raw`(gloas-(?:[A-Fa-f0-9]{65,}|[A-Za-z0-9_-]{64}|[A-Fa-f0-9]{32,}))`,
  String.raw`(gldt-[A-Za-z0-9_-]{20,})`,
  String.raw`(glcbt-[A-Za-z0-9]{1,5}_[A-Za-z0-9_-]{20,})`,
  String.raw`(glptt-[A-Za-z0-9_-]{40,})`,
  String.raw`(glft-(?:[A-Za-z0-9_-]{20,}|[a-h0-9]+-[0-9]+_))`,
  String.raw`(glimt-[A-Za-z0-9_-]{25,})`,
  String.raw`(glagent-[A-Za-z0-9_-]{50,})`,
  String.raw`(glwt-[A-Za-z0-9_-]{20,})`,
  String.raw`(glsoat-[A-Za-z0-9_-]{20,})`,
  String.raw`(glffct-[A-Za-z0-9_-]{20,})`,
  String.raw`(glrt-[A-Za-z0-9._-]{20,})`,
  String.raw`(glrtr?-[A-Za-z0-9_-]{27,300}\.[0-9a-z]{2}\.[0-9a-z]{9})`,
  String.raw`(GR1348941[A-Za-z0-9_-]{20,})`,
  String.raw`(_gitlab_session=[A-Za-z0-9%._-]{20,})`,
  String.raw`(xox[baprs]-[A-Za-z0-9-]{10,})`,
  String.raw`(xapp-[A-Za-z0-9-]{10,})`,
  String.raw`(https:\/\/hooks\.slack\.com\/(?:services\/T[A-Z0-9]+\/B[A-Z0-9]+|workflows\/T[A-Z0-9]+\/A[A-Z0-9]+\/[0-9]{17,19})\/[A-Za-z0-9]{20,})`,
  String.raw`(https:\/\/discord(?:app)?\.com\/api\/webhooks\/[0-9]{17,20}\/[A-Za-z0-9_-]{60,})`,
  String.raw`discord(?:.|\n|\r){0,40}?\b([A-Za-z0-9_-]{24}\.[A-Za-z0-9_-]{6}\.[A-Za-z0-9_-]{27})\b`,
  String.raw`(gsk_[A-Za-z0-9_-]{10,})`,
  String.raw`(AIza[0-9A-Za-z\-_]{20,})`,
  String.raw`(ya29\.[0-9A-Za-z_\-./+=]{10,})`,
  String.raw`(1//0[0-9A-Za-z_\-./+=]{10,})`,
  String.raw`(GOCSPX-[A-Za-z0-9_-]{20,})`,
  String.raw`(eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,})`,
  String.raw`(pplx-[A-Za-z0-9_-]{10,})`,
  String.raw`(fal_[A-Za-z0-9_-]{10,})`,
  String.raw`${IDENTIFIER_SAFE_TOKEN_BOUNDARY}(fc-[A-Za-z0-9]{10,})`,
  String.raw`(bb_live_[A-Za-z0-9_-]{10,})`,
  base64SafeToken(String.raw`gAAAA[A-Za-z0-9_=-]{20,}`),
  String.raw`(sk_live_[A-Za-z0-9]{10,})`,
  String.raw`(sk_test_[A-Za-z0-9]{10,})`,
  String.raw`(rk_live_[A-Za-z0-9]{10,})`,
  String.raw`(SG\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,})`,
  String.raw`(npm_[A-Za-z0-9]{10,})`,
  String.raw`(pypi-[A-Za-z0-9_-]{10,})`,
  String.raw`(dop_v1_[A-Za-z0-9]{10,})`,
  String.raw`(doo_v1_[A-Za-z0-9]{10,})`,
  String.raw`(dor_v1_[A-Za-z0-9]{10,})`,
  String.raw`(dp\.(?:ct|pt|sa|scim|audit)\.[A-Za-z0-9]{40,44})`,
  String.raw`(dp\.st\.[A-Za-z0-9]{40,44})`,
  String.raw`(dp\.st\.[a-z0-9_-]{2,35}\.[A-Za-z0-9]{40,44})`,
  String.raw`(dckr_(?:pat|oat)_[A-Za-z0-9_-]{27,32})`,
  String.raw`(bkua_[a-z0-9]{40})`,
  String.raw`(CCIPAT_[A-Za-z0-9]{22}_[A-Fa-f0-9]{40})`,
  String.raw`(sbp_[a-z0-9]{40})`,
  base64SafeToken(String.raw`dapi[0-9a-f]{32}(?:-\d)?`),
  String.raw`(dd[pw]_[A-Za-z0-9]{36})`,
  String.raw`(glsa_[A-Za-z0-9_]{41})`,
  String.raw`(glc_eyJ[A-Za-z0-9+/=]{60,160})`,
  String.raw`(nfp_[A-Za-z0-9_]{36})`,
  String.raw`(CFPAT-[A-Za-z0-9_\-]{40,})`,
  base64SafeToken(String.raw`ATCTT3xFfG[A-Za-z0-9+/=_-]+=[A-Za-z0-9]{8}`),
  base64SafeToken(String.raw`ATATT[A-Za-z0-9+/=_-]+=[A-Za-z0-9]{8}`),
  base64SafeToken(String.raw`ATBB[A-Za-z0-9_=.-]{16,}`),
  String.raw`(BBDC-[A-Za-z0-9+/@_-]{40,50})`,
  String.raw`(HRKU-AA[A-Za-z0-9_-]{20,})`,
  String.raw`(pat-(?:eu|na)1-[A-Za-z0-9]{8}\-[A-Za-z0-9]{4}\-[A-Za-z0-9]{4}\-[A-Za-z0-9]{4}\-[A-Za-z0-9]{12})`,
  String.raw`(apify_api_[A-Za-z0-9\-]{20,})`,
  String.raw`(FlyV1 fm\d+_[A-Za-z0-9+/=,_-]{100,})`,
  String.raw`(fio-u-[A-Za-z0-9_-]{40,})`,
  String.raw`(^|[^A-Za-z0-9_])(am_[A-Za-z0-9_-]{10,})`,
  String.raw`(^|[^A-Za-z0-9_])(sk_[A-Za-z0-9_]{10,})`,
  String.raw`(tvly-[A-Za-z0-9]{10,})`,
  String.raw`(exa_[A-Za-z0-9]{10,})`,
  String.raw`(syt_[A-Za-z0-9]{10,})`,
  String.raw`(retaindb_[A-Za-z0-9]{10,})`,
  String.raw`(hsk-[A-Za-z0-9]{10,})`,
  String.raw`(mem0_[A-Za-z0-9]{10,})`,
  String.raw`(brv_[A-Za-z0-9]{10,})`,
  String.raw`(xai-[A-Za-z0-9]{30,})`,
  String.raw`${IDENTIFIER_SAFE_TOKEN_BOUNDARY}(fw-[A-Za-z0-9]{30,})`,
  String.raw`${IDENTIFIER_SAFE_TOKEN_BOUNDARY}(fw_[A-Za-z0-9]{30,})`,
  String.raw`${IDENTIFIER_SAFE_TOKEN_BOUNDARY}(fpk_[A-Za-z0-9]{30,})`,
  base64SafeToken(String.raw`AKIA[A-Z0-9]{16}`),
  base64SafeToken(String.raw`ASIA[A-Z0-9]{16}`),
  String.raw`(AKID[A-Za-z0-9]{10,})`,
  String.raw`(LTAI[A-Za-z0-9]{10,})`,
  String.raw`(hf_[A-Za-z0-9]{10,})`,
  String.raw`(api_org_[A-Za-z0-9]{20,})`,
  String.raw`(r8_[A-Za-z0-9]{10,})`,
  TELEGRAM_BOT_TOKEN_REDACT_PATTERN,
  TELEGRAM_TOKEN_REDACT_PATTERN,
];

export const DEFAULT_REDACT_PATTERNS: readonly RedactPattern[] = [
  ...DEFAULT_REDACT_FIELD_PATTERNS,
  ...VENDOR_TOKEN_REDACT_PATTERNS,
  AWS_SECRET_ACCESS_KEY_MATCHER,
];

// Browser callers share the canonical patterns without constructing backend-only projections.
export function createBackendRedactPatterns() {
  const bodySecretKeys = new Set([
    "access_token",
    "auth_token",
    "awssecretaccesskey",
    "aws_secret_access_key",
    "hook_token",
    "refresh_token",
    "id_token",
    "token",
    "api_key",
    "apikey",
    "client_secret",
    "app_secret",
    "password",
    "pass",
    "passwd",
    "auth",
    "jwt",
    "session",
    "code",
    "signature",
    "x_amz_signature",
    "x_amz_security_token",
    "secret",
    "secretaccesskey",
    "credential",
    "private_key",
    "authorization",
    "key",
    "card_number",
    "card_cvc",
    "card_cvv",
    "cvc",
    "cvv",
    "security_code",
    "payment_credential",
    "shared_payment_token",
  ]);
  const credentialHeaderField = new RegExp(
    `^(?:${CREDENTIAL_STYLE_HEADER_KEYS}|${GATEWAY_SECURITY_HEADER_KEYS})$`,
    "i",
  );
  const formAware = new Set([
    CREDENTIAL_STYLE_EQUALS_ASSIGNMENT_REDACT_PATTERN,
    GATEWAY_SECURITY_EQUALS_ASSIGNMENT_REDACT_PATTERN,
  ]);
  const shellReferencePreserving = new Set([
    ENV_ASSIGNMENT_REDACT_PATTERN,
    ESCAPED_ENV_ASSIGNMENT_REDACT_PATTERN,
    STANDALONE_ASSIGNMENT_QUOTED_REDACT_PATTERN,
    STANDALONE_ASSIGNMENT_REDACT_PATTERN,
  ]);
  // Necessary conditions only: the canonical rules still decide boundaries, captures and masks.
  // Probes are stateless and run against each rule's current input, including earlier replacements.
  const CONFIG_KEY_PROBE = new RegExp(
    String.raw`[:=](?<=(?:${CONFIG_ASSIGNMENT_SECRET_KEYS})\s*[:=])`,
  );
  const CONFIG_QUOTED_PROBE = new RegExp(
    String.raw`[:=](?<=(?:${CONFIG_ASSIGNMENT_SECRET_KEYS})\s*[:=])\s*["'\x60]`,
  );
  const CONFIG_NAMESPACED_PROBE = new RegExp(
    String.raw`[:=](?<=\.(?:${CONFIG_ASSIGNMENT_SECRET_KEYS})\s*[:=])`,
  );
  const STANDALONE_ASSIGNMENT_PROBE = new RegExp(
    String.raw`=(?<=(?:^|[\s,;({\["])(?:${STANDALONE_ASSIGNMENT_SECRET_KEYS})=)`,
    "i",
  );
  const prefilters = new Map<string, (text: string) => boolean>([
    [
      ENV_ASSIGNMENT_REDACT_PATTERN,
      (text) => /KEY|TOKEN|SECRET|PASSWORD|PASSWD|CARD|CVC|CVV|SECURITY|PAYMENT/.test(text),
    ],
    [ESCAPED_ENV_ASSIGNMENT_REDACT_PATTERN, (text) => text.includes("\\")],
    [STANDALONE_ASSIGNMENT_QUOTED_REDACT_PATTERN, (text) => /[=]["'`]/.test(text)],
    [STANDALONE_ASSIGNMENT_REDACT_PATTERN, (text) => STANDALONE_ASSIGNMENT_PROBE.test(text)],
    [
      CONFIG_QUOTED_ASSIGNMENT_REDACT_PATTERN,
      (text) => /["'`]/.test(text) && CONFIG_QUOTED_PROBE.test(text),
    ],
    [
      CONFIG_ASSIGNMENT_REDACT_PATTERN,
      (text) => (text.includes(":") || text.includes("=")) && CONFIG_KEY_PROBE.test(text),
    ],
    [CONFIG_DIRECT_ASSIGNMENT_REDACT_PATTERN, (text) => text.includes("=")],
    [
      CONFIG_PREFIXED_PASSWORD_ASSIGNMENT_REDACT_PATTERN,
      (text) => /[-_](?:password|passphrase|pass|passwd)\s*[:=]/.test(text),
    ],
    [CONFIG_NAMESPACED_ASSIGNMENT_REDACT_PATTERN, (text) => CONFIG_NAMESPACED_PROBE.test(text)],
    [AMBIGUOUS_QUOTED_SECRET_FIELD_REDACT_PATTERN, (text) => /["']/.test(text)],
    [AMBIGUOUS_QUOTED_AUTH_FIELD_REDACT_PATTERN, (text) => /["']/.test(text)],
    ...HTTP_AUTH_HEADER_REDACT_PATTERNS.slice(0, 4).map(
      (raw): [string, (text: string) => boolean] => [raw, (text) => /authorization/i.test(text)],
    ),
    [
      CREDENTIAL_STYLE_COLON_HEADER_REDACT_PATTERN,
      (text) => /api-key|apikey|api-token|access-token/i.test(text),
    ],
    [
      CREDENTIAL_STYLE_EQUALS_ASSIGNMENT_REDACT_PATTERN,
      (text) => /api-key|apikey|api-token|access-token/i.test(text),
    ],
    [STRUCTURED_JSON_SECRET_REDACT_PATTERN, (text) => text.includes('"')],
    [STRUCTURED_JSON_PAYMENT_REDACT_PATTERN, (text) => text.includes('"')],
    [
      GATEWAY_SECURITY_COLON_HEADER_REDACT_PATTERN,
      (text) => /x-openclaw-token|x-pomerium-jwt-assertion|x-api-key|x-auth-token/i.test(text),
    ],
    [
      GATEWAY_SECURITY_EQUALS_ASSIGNMENT_REDACT_PATTERN,
      (text) => /x-openclaw-token|x-pomerium-jwt-assertion|x-api-key|x-auth-token/i.test(text),
    ],
  ]);
  const chunkUnsafe = new Set([
    TELEGRAM_BOT_TOKEN_REDACT_PATTERN,
    TELEGRAM_TOKEN_REDACT_PATTERN,
    AUTHORIZATION_BEARER_REDACT_PATTERN,
    AUTHORIZATION_BASIC_REDACT_PATTERN,
    AUTHORIZATION_BOT_REDACT_PATTERN,
    STANDALONE_BEARER_REDACT_PATTERN,
    ...HTTP_AUTH_HEADER_REDACT_PATTERNS,
    ...VENDOR_TOKEN_REDACT_PATTERNS.filter((source) =>
      source.startsWith(BASE64_SAFE_TOKEN_BOUNDARY),
    ),
  ]);

  return {
    bodySecretKeys,
    credentialHeaderField,
    formAware,
    shellReferencePreserving,
    prefilters,
    chunkUnsafe,
  };
}
